<template>
  <motion.div
    :initial="animationVariants.container.initial"
    :animate="animationVariants.container.animate"
    :exit="animationVariants.container.exit"
    :transition="animationVariants.container.transition"
    :style="containerStyle"
    class="flex justify-center items-center w-full"
  >
    <!-- Spinning Dots Loader -->
    <div v-if="variant === 'dots'" class="flex space-x-2">
      <motion.div
        v-for="i in 3"
        :key="i"
        :initial="animationVariants.dots.initial"
        :animate="animationVariants.dots.animate"
        :transition="{
          ...animationVariants.dots.transition,
          delay: (i - 1) * 0.2
        }"
        class="w-3 h-3 bg-gray-300 rounded-full"
      />
    </div>

    <!-- Pulse Circle Loader -->
    <div v-else-if="variant === 'pulse'" class="relative">
      <motion.div
        :initial="animationVariants.pulse.initial"
        :animate="animationVariants.pulse.animate"
        :transition="animationVariants.pulse.transition"
        class="w-12 h-12 border-4 border-gray-300 rounded-full opacity-20"
      />
      <motion.div
        :initial="animationVariants.innerPulse.initial"
        :animate="animationVariants.innerPulse.animate"
        :transition="animationVariants.innerPulse.transition"
        class="absolute inset-2 border-2 border-gray-300 rounded-full"
      />
    </div>

    <!-- Morphing Squares -->
    <div v-else-if="variant === 'morph'" class="grid grid-cols-2 gap-1">
      <motion.div
        v-for="i in 4"
        :key="i"
        :initial="animationVariants.morph.initial"
        :animate="animationVariants.morph.animate"
        :transition="{
          ...animationVariants.morph.transition,
          delay: (i - 1) * 0.1
        }"
        class="w-3 h-3 bg-gray-300"
      />
    </div>

    <!-- Elegant Spinner (Default) -->
    <div v-else class="relative">
      <motion.div
        :initial="animationVariants.spinner.initial"
        :animate="animationVariants.spinner.animate"
        :transition="animationVariants.spinner.transition"
        class="w-8 h-8 border-2 border-transparent border-t-gray-300 rounded-full"
      />
      <motion.div
        :initial="animationVariants.glow.initial"
        :animate="animationVariants.glow.animate"
        :transition="animationVariants.glow.transition"
        class="absolute inset-0 w-8 h-8 rounded-full bg-gray-300 opacity-10"
      />
    </div>

    <!-- Loading Text (Optional) -->
    <motion.div
      v-if="showText"
      :initial="{ opacity: 0, y: 10 }"
      :animate="{ opacity: 1, y: 0 }"
      :transition="{ delay: 0.3, duration: 0.3 }"
      class="ml-3 text-sm font-medium"
    >
      {{ loadingText }}
    </motion.div>
  </motion.div>
</template>

<script>
import { defineComponent } from 'vue';
import { motion } from 'motion-v';

export default defineComponent({
  name: 'MotionLoader',
  components: {
    motion
  },
  props: {
    variant: {
      type: String,
      default: 'elegant',
      validator: (value) => ['elegant', 'dots', 'pulse', 'morph'].includes(value)
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg', 'xl'].includes(value)
    },
    color: {
      type: String,
      default: 'text-gray-400'
    },
    showText: {
      type: Boolean,
      default: false
    },
    loadingText: {
      type: String,
      default: 'Loading...'
    }
  },
  computed: {
    containerStyle() {
      const sizes = {
        sm: { padding: '1rem', fontSize: '0.875rem' },
        md: { padding: '2rem', fontSize: '1rem' },
        lg: { padding: '3rem', fontSize: '1.125rem' },
        xl: { padding: '4rem', fontSize: '1.25rem' }
      };
      return {
        ...sizes[this.size],
        color: 'currentColor'
      };
    },
    animationVariants() {
      return {
        container: {
          initial: { opacity: 0, scale: 0.9 },
          animate: { opacity: 1, scale: 1 },
          exit: { opacity: 0, scale: 0.95 },
          transition: {
            duration: 0.15,
            ease: 'easeOut'
          }
        },
        dots: {
          initial: { scale: 0, opacity: 0 },
          animate: { scale: [0, 1.2, 1], opacity: [0, 1, 0.7] },
          transition: {
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }
        },
        pulse: {
          initial: { scale: 0.8, opacity: 0.3 },
          animate: { scale: [0.8, 1.2, 0.8], opacity: [0.3, 0.1, 0.3] },
          transition: {
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }
        },
        innerPulse: {
          initial: { scale: 1, opacity: 0.8 },
          animate: { scale: [1, 0.7, 1], opacity: [0.8, 0.3, 0.8] },
          transition: {
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }
        },
        morph: {
          initial: { scale: 1, borderRadius: '0%' },
          animate: { 
            scale: [1, 0.8, 1], 
            borderRadius: ['0%', '50%', '0%'],
            rotate: [0, 90, 180]
          },
          transition: {
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }
        },
        spinner: {
          initial: { rotate: 0 },
          animate: { rotate: 360 },
          transition: {
            duration: 1,
            repeat: Infinity,
            ease: 'linear'
          }
        },
        glow: {
          initial: { scale: 1, opacity: 0.1 },
          animate: { scale: [1, 1.5, 1], opacity: [0.1, 0.3, 0.1] },
          transition: {
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }
        }
      };
    }
  }
});
</script>

<style scoped>
/* Additional styling for better visual hierarchy */
.motion-loader {
  transition: all 0.3s ease;
}
</style> 